import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';

const AddStudent = ({ onClose }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    contact: "",
    zipcode: "",
    dob: "",
    gender: "",
    city_id: "",
    assignSchool: "",
    course_id: "",
    enrollment_for: "self",
    admin_exact_start_at: "",
    admin_time_slot: "",
    inc_sat_sun: true,
    admin_note: "",
    type: "V"
  });

  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [courses, setCourses] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const { bearerAuth, publicAuth } = useApi();

  const getLatestAllowedDob = () => {
    const today = new Date();
    today.setFullYear(today.getFullYear() - 18);
    return today.toISOString().split("T")[0];
  };

  const isAtLeast18 = (dob) => {
    if (!dob) return false;
    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18;
  };

  // Fetch cities and courses on component mount
  useEffect(() => {
    fetchCities();
    fetchCourses();
  }, []);

  // Fetch schools when city is selected
  useEffect(() => {
    if (formData.city_id) {
      fetchSchoolsForCity(formData.city_id);
    } else {
      setSchools([]);
      setFormData(prev => ({ ...prev, assignSchool: "" }));
      setTimeSlots([]);
    }
  }, [formData.city_id]);

  useEffect(() => {
    if (!formData.city_id) {
      fetchCourses();
      return;
    }
    const selectedCity = cities.find((c) => String(c.id) === String(formData.city_id));
    if (selectedCity?.name) {
      fetchCourses(selectedCity.name);
    } else {
      fetchCourses();
    }
  }, [formData.city_id, cities]);

  // Fetch time slots when date and school are selected
  useEffect(() => {
    if (formData.admin_exact_start_at && formData.assignSchool) {
      const date = new Date(formData.admin_exact_start_at);
      if (date) {
        fetchTimeSlots(date);
      }
    } else {
      setTimeSlots([]);
      setFormData(prev => ({ ...prev, admin_time_slot: "" }));
    }
  }, [formData.admin_exact_start_at, formData.assignSchool]);

  const fetchCities = async () => {
    try {
      const res = await publicAuth("cities", "GET");
      const list = res?.data?.data || res?.data?.cities || [];
      setCities(list);
    } catch (e) {
      console.error("cities fetch error:", e);
      setCities([]);
    }
  };

  const fetchSchoolsForCity = async (cityId) => {
    try {
      setApiLoading(true);
      const res = await publicAuth("driving-schools", "POST", { city_id: cityId });
      const list = res?.data?.data || res?.data?.schools || [];
      console.log("Schools fetched:", list);
      setSchools(list);
    } catch (e) {
      console.error("schools fetch error:", e);
      setSchools([]);
    } finally {
      setApiLoading(false);
    }
  };

  const fetchCourses = async (cityName = "") => {
    try {
      const url = cityName ? `courses?city=${encodeURIComponent(cityName)}` : "courses";
      const res = await publicAuth(url);
      const list = res.data?.courses || res.data?.data || [];
      console.log("Courses fetched:", list);
      setCourses(list);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    }
  };

  const getCourseDisplayPrice = (course) => {
    const value = course?.price?.total_price ?? course?.total_price ?? course?.price;
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) return "Price N/A";
    return `₹${Math.round(numeric)}`;
  };

  const fetchTimeSlots = async (date) => {
    try {
      setApiLoading(true);
      const formattedDate = date.toISOString().split("T")[0];
      console.log("Fetching time slots for:", {
        date: formattedDate,
        assignSchool: formData.assignSchool
      });
      
      const res = await bearerAuth(`check-timeslots`, "POST", {
        date: formattedDate,
        assignSchool: formData.assignSchool,
      });
      
      console.log("Time slots response:", res.data);
      
      // Check different response formats
      let slots = [];
      if (res?.data?.slots) {
        slots = res.data.slots;
      } else if (res?.data?.timeslots) {
        slots = res.data.timeslots;
      } else if (res?.data?.data) {
        slots = res.data.data;
      }
      
      console.log("Parsed time slots:", slots);
      setTimeSlots(slots);
    } catch (err) {
      console.error("Time slots fetch error:", err);
      setTimeSlots([]);
    } finally {
      setApiLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is modified
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.contact) newErrors.contact = "Contact is required";
    if (!formData.zipcode) newErrors.zipcode = "Zipcode is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";
    else if (!isAtLeast18(formData.dob)) newErrors.dob = "Student must be at least 18 years old";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.city_id) newErrors.city_id = "City is required";
    if (!formData.assignSchool) newErrors.assignSchool = "School is required";
    if (!formData.course_id) newErrors.course_id = "Course is required";
    if (!formData.admin_exact_start_at) newErrors.admin_exact_start_at = "Start date is required";
    if (!formData.admin_time_slot) newErrors.admin_time_slot = "Time slot is required";
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Contact validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (formData.contact && !phoneRegex.test(formData.contact.replace(/\D/g, ''))) {
      newErrors.contact = "Please enter a valid 10-digit phone number";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        contact: formData.contact.toString(),
        course_id: parseInt(formData.course_id),
        city_id: parseInt(formData.city_id),
        assignSchool: formData.assignSchool
      };
      
      console.log("Submitting payload:", payload);
      
      const response = await bearerAuth("driving-school/add-student", "POST", payload);
      
      console.log("Add student response:", response.data);
      
     if (response.data?.status === true) {
  alert("Student added successfully!");
  onClose();
} else {
  setApiError(response.data?.message);
}
    } catch (error) {
  console.error("Error adding student:", error);

  const message =
    error.response?.data?.message || "Something went wrong";

  setApiError(message);
} finally {
      setLoading(false);
    }
  };

  // Format time in 24-hour format (HH:MM:SS)
  const formatTime24Hour = (timeString) => {
    if (!timeString) return '';
    // If time is already in HH:MM:SS format, return as is
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      // Ensure we have HH:MM:SS format
      if (parts.length === 3) {
        return timeString;
      } else if (parts.length === 2) {
        return `${timeString}:00`;
      }
    }
    return timeString;
  };

  // Get time slot display text in 24-hour format
  const getTimeSlotDisplay = (slot) => {
    if (typeof slot === 'object') {
      if (slot.start_time && slot.end_time) {
        const base = `${formatTime24Hour(slot.start_time)} - ${formatTime24Hour(slot.end_time)}`;
        const capacity = Number(slot.capacity || 0);
        const bookedCount = Number(slot.booked_count || 0);
        if (capacity > 0) {
          return `${base} (${bookedCount}/${capacity})${slot.booked ? ' - Full' : ''}`;
        }
        return base;
      }
      return JSON.stringify(slot);
    }
    return slot;
  };

  // Get time slot value for form - store as "start_time-end_time"
  const getTimeSlotValue = (slot) => {
    if (typeof slot === 'object' && slot.start_time && slot.end_time) {
      return `${formatTime24Hour(slot.start_time)} - ${formatTime24Hour(slot.end_time)}`;
    }
    return slot;
  };

  const isSlotFull = (slot) => {
    if (typeof slot !== 'object') return false;
    if (slot.booked === true) return true;
    const capacity = Number(slot.capacity || 0);
    const bookedCount = Number(slot.booked_count || 0);
    return capacity > 0 && bookedCount >= capacity;
  };

  // Get course duration display
  const getCourseDuration = (course) => {
    if (course.duration) {
      if (course.duration === 1) {
        return `(${course.duration} day)`;
      } else if (course.duration > 1) {
        return `(${course.duration} days)`;
      }
    }
    return '';
  };

  return (
    <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">Add New Student</h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
              disabled={loading || apiLoading}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {apiError && (
  <div className="alert alert-danger">
    {apiError}
  </div>
)}
              <div className="row g-3">
                {/* Personal Information */}
                <div className="col-md-6">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.full_name ? 'is-invalid' : ''}`}
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    disabled={loading || apiLoading}
                  />
                  {errors.full_name && <div className="invalid-feedback">{errors.full_name}</div>}
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email"
                    disabled={loading || apiLoading}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Contact Number *</label>
                  <input
                    type="tel"
                    className={`form-control ${errors.contact ? 'is-invalid' : ''}`}
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                    placeholder="Enter 10-digit contact number"
                    maxLength="10"
                    disabled={loading || apiLoading}
                  />
                  {errors.contact && <div className="invalid-feedback">{errors.contact}</div>}
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Zipcode *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.zipcode ? 'is-invalid' : ''}`}
                    name="zipcode"
                    value={formData.zipcode}
                    onChange={handleChange}
                    placeholder="Enter zipcode"
                    disabled={loading || apiLoading}
                  />
                  {errors.zipcode && <div className="invalid-feedback">{errors.zipcode}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Date of Birth *</label>
                  <input
                    type="date"
                    className={`form-control ${errors.dob ? 'is-invalid' : ''}`}
                    name="dob"
                    value={formData.dob}
                    max={getLatestAllowedDob()}
                    onChange={handleChange}
                    disabled={loading || apiLoading}
                  />
                  {errors.dob && <div className="invalid-feedback">{errors.dob}</div>}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Gender *</label>
                  <select
                    className={`form-select ${errors.gender ? 'is-invalid' : ''}`}
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    disabled={loading || apiLoading}
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                </div>
                
                {/* City and School Selection */}
                <div className="col-md-6">
                  <label className="form-label">City *</label>
                  <select
                    className={`form-select ${errors.city_id ? 'is-invalid' : ''}`}
                    name="city_id"
                    value={formData.city_id}
                    onChange={handleChange}
                    disabled={loading || apiLoading}
                  >
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>
                        {city.name || city.city_name}
                      </option>
                    ))}
                  </select>
                  {errors.city_id && <div className="invalid-feedback">{errors.city_id}</div>}
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">School *</label>
                  <div className="position-relative">
                    <select
                      className={`form-select ${errors.assignSchool ? 'is-invalid' : ''}`}
                      name="assignSchool"
                      value={formData.assignSchool}
                      onChange={handleChange}
                      disabled={!formData.city_id || schools.length === 0 || loading || apiLoading}
                    >
                      <option value="">Select School</option>
                      {schools.map(school => (
                        <option key={school.key || school.id} value={school.key || school.id}>
                          {school.name} ({school.city_name || ''})
                        </option>
                      ))}
                    </select>
                    {apiLoading && schools.length === 0 && formData.city_id && (
                      <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                        <span className="spinner-border spinner-border-sm text-primary"></span>
                      </div>
                    )}
                  </div>
                  {errors.assignSchool && <div className="invalid-feedback d-block">{errors.assignSchool}</div>}
                  {formData.city_id && !apiLoading && schools.length === 0 && (
                    <small className="text-muted">No schools available for selected city</small>
                  )}
                </div>
                
                {/* Course and Enrollment */}
                <div className="col-md-6">
                  <label className="form-label">Course *</label>
                  <select
                    className={`form-select ${errors.course_id ? 'is-invalid' : ''}`}
                    name="course_id"
                    value={formData.course_id}
                    onChange={handleChange}
                    disabled={loading || apiLoading}
                  >
                    <option value="">Select Course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>
                        {(course.title || course.name || course.course_name)}
                        {" "}
                        {getCourseDuration(course)}
                        {" - "}
                        {getCourseDisplayPrice(course)}
                      </option>
                    ))}
                  </select>
                  {errors.course_id && <div className="invalid-feedback">{errors.course_id}</div>}
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Enrollment For *</label>
                  <select
                    className="form-select"
                    name="enrollment_for"
                    value={formData.enrollment_for}
                    onChange={handleChange}
                    disabled={loading || apiLoading}
                  >
                    <option value="self">Self</option>
                    <option value="parent">Parent</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                {/* Date and Time Selection */}
                <div className="col-md-6">
                  <label className="form-label">Start Date *</label>
                  <input
                    type="date"
                    className={`form-control ${errors.admin_exact_start_at ? 'is-invalid' : ''}`}
                    name="admin_exact_start_at"
                    value={formData.admin_exact_start_at}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={loading || apiLoading}
                  />
                  {errors.admin_exact_start_at && <div className="invalid-feedback">{errors.admin_exact_start_at}</div>}
                </div>
                
                <div className="col-md-6">
                  <label className="form-label">Time Slot *</label>
                  <div className="position-relative">
                    <select
                      className={`form-select ${errors.admin_time_slot ? 'is-invalid' : ''}`}
                      name="admin_time_slot"
                      value={formData.admin_time_slot}
                      onChange={handleChange}
                      disabled={!formData.admin_exact_start_at || !formData.assignSchool || timeSlots.length === 0 || loading || apiLoading}
                    >
                      <option value="">Select Time Slot</option>
                      {timeSlots.map((slot, index) => {
                        const slotValue = getTimeSlotValue(slot);
                        const slotFull = isSlotFull(slot);
                        return (
                          <option
                            key={slot.id || index}
                            value={slotValue}
                            disabled={slotFull}
                            className={slotFull ? 'text-muted' : ''}
                          >
                            {getTimeSlotDisplay(slot)}
                            {slotFull ? ' (Booked)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    {apiLoading && formData.admin_exact_start_at && formData.assignSchool && timeSlots.length === 0 && (
                      <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                        <span className="spinner-border spinner-border-sm text-primary"></span>
                      </div>
                    )}
                  </div>
                  {errors.admin_time_slot && <div className="invalid-feedback d-block">{errors.admin_time_slot}</div>}
                  {formData.admin_exact_start_at && formData.assignSchool && !apiLoading && timeSlots.length === 0 && (
                    <small className="text-muted">No time slots available for selected date</small>
                  )}
                  {timeSlots.length > 0 && (
                    <small className="text-muted">
                      Available slots: {timeSlots.filter(slot => !isSlotFull(slot)).length} / {timeSlots.length}
                    </small>
                  )}
                </div>
                
                {/* Additional Options */}
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="inc_sat_sun"
                      checked={formData.inc_sat_sun}
                      onChange={handleChange}
                      id="incSatSun"
                      disabled={loading || apiLoading}
                    />
                    <label className="form-check-label" htmlFor="incSatSun">
                      Include Saturdays and Sundays
                    </label>
                  </div>
                </div>
                
                <div className="col-12">
                  <label className="form-label">Admin Notes</label>
                  <textarea
                    className="form-control"
                    name="admin_note"
                    value={formData.admin_note}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Add any special instructions or notes"
                    disabled={loading || apiLoading}
                  />
                  <small className="text-muted">Example: Please bring your learner's permit on the first day.</small>
                </div>
                
                {/* Hidden field for type */}
                <input type="hidden" name="type" value="V" />
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={loading || apiLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || apiLoading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Adding...
                  </>
                ) : 'Add Student'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddStudent;
